import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, MapPin, Map as MapIcon, Layers, Vote, RefreshCw, Lock, Unlock, FileDown, Upload, ShieldCheck, Info, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function WilayahPage() {
  const [dusuns, setDusuns] = useState([]);
  const [rws, setRws] = useState([]);
  const [rts, setRts] = useState([]);
  const [tps, setTps] = useState([]);

  const loadAll = async () => {
    try {
      const [d, r, rt, t] = await Promise.all([
        api.get("/dusuns"), api.get("/rws"), api.get("/rts"), api.get("/tps?include_progress=true"),
      ]);
      setDusuns(d.data || []); setRws(r.data || []); setRts(rt.data || []); setTps(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="space-y-6" data-testid="wilayah-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Manajemen Wilayah</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Dusun, RW, RT &amp; TPS</h1>
        <p className="text-slate-600 text-sm mt-2">Kelola master data wilayah secara hierarki: Dusun → RW → RT.</p>
      </header>

      <Tabs defaultValue="dusun" className="w-full">
        <TabsList className="grid grid-cols-4 max-w-xl">
          <TabsTrigger value="dusun" data-testid="tab-dusun"><MapIcon className="h-3.5 w-3.5 mr-1" />Dusun ({dusuns.length})</TabsTrigger>
          <TabsTrigger value="rw" data-testid="tab-rw"><Layers className="h-3.5 w-3.5 mr-1" />RW ({rws.length})</TabsTrigger>
          <TabsTrigger value="rt" data-testid="tab-rt"><MapPin className="h-3.5 w-3.5 mr-1" />RT ({rts.length})</TabsTrigger>
          <TabsTrigger value="tps" data-testid="tab-tps"><Vote className="h-3.5 w-3.5 mr-1" />TPS ({tps.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="dusun" className="mt-4"><DusunTab dusuns={dusuns} reload={loadAll} /></TabsContent>
        <TabsContent value="rw" className="mt-4"><RwTab dusuns={dusuns} rws={rws} reload={loadAll} /></TabsContent>
        <TabsContent value="rt" className="mt-4"><RtTab dusuns={dusuns} rws={rws} rts={rts} reload={loadAll} /></TabsContent>
        <TabsContent value="tps" className="mt-4"><TpsTab dusuns={dusuns} tpsList={tps} reload={loadAll} /></TabsContent>
      </Tabs>
    </div>
  );
}

function DusunTab({ dusuns, reload }) {
  const [nama, setNama] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!nama.trim()) return;
    try { await api.post("/dusuns", { nama: nama.trim() }); toast.success("Dusun ditambahkan."); setNama(""); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (d) => {
    if (!window.confirm(`Hapus ${d.nama}?`)) return;
    try { await api.delete(`/dusuns/${d.id}`); toast.success("Dihapus."); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="rounded-md border border-slate-200 bg-white p-4 flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs font-semibold">Nama Dusun Baru</Label>
          <Input value={nama} onChange={(e) => setNama(e.target.value)} placeholder="cth: Dusun Mawar" className="h-11" data-testid="dusun-name-input" />
        </div>
        <Button type="submit" className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="add-dusun-button">
          <Plus className="h-4 w-4 mr-2" /> Tambah
        </Button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {dusuns.map((d) => (
          <div key={d.id} className="rounded-md border border-slate-200 bg-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-slate-100 grid place-items-center"><MapIcon className="h-4 w-4 text-slate-700" /></div>
              <div className="font-display font-bold">{d.nama}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => del(d)} className="text-red-700"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RwTab({ dusuns, rws, reload }) {
  const [form, setForm] = useState({ dusun: "", kode: "", keterangan: "" });
  const [filterDusun, setFilterDusun] = useState("all");
  const add = async (e) => {
    e.preventDefault();
    if (!form.dusun || !form.kode) { toast.error("Dusun dan Kode RW wajib"); return; }
    try { await api.post("/rws", form); toast.success("RW ditambahkan."); setForm({ dusun: form.dusun, kode: "", keterangan: "" }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (rw) => {
    if (!window.confirm(`Hapus RW ${rw.kode} di ${rw.dusun}? RT di dalamnya juga akan terhapus.`)) return;
    try { await api.delete(`/rws/${rw.id}`); toast.success("Dihapus."); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const filtered = filterDusun === "all" ? rws : rws.filter((r) => r.dusun === filterDusun);
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="rounded-md border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Select value={form.dusun} onValueChange={(v) => setForm({ ...form, dusun: v })}>
          <SelectTrigger className="h-11" data-testid="rw-dusun-select"><SelectValue placeholder="Pilih Dusun" /></SelectTrigger>
          <SelectContent>{dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} placeholder="Kode RW (001)" className="h-11 font-mono" data-testid="rw-kode-input" />
        <Input value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} placeholder="Keterangan (opsional)" className="h-11" />
        <Button type="submit" className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="add-rw-button">
          <Plus className="h-4 w-4 mr-2" /> Tambah RW
        </Button>
      </form>

      <div className="flex items-center gap-3">
        <Label className="text-xs font-semibold">Filter Dusun:</Label>
        <Select value={filterDusun} onValueChange={setFilterDusun}>
          <SelectTrigger className="h-10 w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            {dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Dusun</th>
              <th className="text-left px-4 py-3 font-semibold">Kode RW</th>
              <th className="text-left px-4 py-3 font-semibold">Keterangan</th>
              <th className="text-right px-4 py-3 font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">Belum ada RW.</td></tr>}
            {filtered.map((rw) => (
              <tr key={rw.id} className="border-t border-slate-200">
                <td className="px-4 py-3">{rw.dusun}</td>
                <td className="px-4 py-3 font-mono font-bold">RW {rw.kode}</td>
                <td className="px-4 py-3 text-slate-600">{rw.keterangan || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => del(rw)} className="text-red-700"><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RtTab({ dusuns, rws, rts, reload }) {
  const [form, setForm] = useState({ dusun: "", rw: "", kode: "", keterangan: "" });
  const [filterDusun, setFilterDusun] = useState("all");
  const [filterRw, setFilterRw] = useState("all");
  const rwsInDusun = form.dusun ? rws.filter((r) => r.dusun === form.dusun) : [];
  const add = async (e) => {
    e.preventDefault();
    if (!form.dusun || !form.rw || !form.kode) { toast.error("Dusun, RW, Kode RT wajib"); return; }
    try { await api.post("/rts", form); toast.success("RT ditambahkan."); setForm({ dusun: form.dusun, rw: form.rw, kode: "", keterangan: "" }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (rt) => {
    if (!window.confirm(`Hapus RT ${rt.kode}?`)) return;
    try { await api.delete(`/rts/${rt.id}`); toast.success("Dihapus."); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  let filtered = rts;
  if (filterDusun !== "all") filtered = filtered.filter((r) => r.dusun === filterDusun);
  if (filterRw !== "all") filtered = filtered.filter((r) => r.rw === filterRw);
  const rwsFiltered = filterDusun === "all" ? rws : rws.filter((r) => r.dusun === filterDusun);
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="rounded-md border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <Select value={form.dusun} onValueChange={(v) => setForm({ ...form, dusun: v, rw: "" })}>
          <SelectTrigger className="h-11" data-testid="rt-dusun-select"><SelectValue placeholder="Dusun" /></SelectTrigger>
          <SelectContent>{dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={form.rw} onValueChange={(v) => setForm({ ...form, rw: v })} disabled={!form.dusun}>
          <SelectTrigger className="h-11" data-testid="rt-rw-select"><SelectValue placeholder="RW" /></SelectTrigger>
          <SelectContent>{rwsInDusun.map((r) => <SelectItem key={r.id} value={r.kode}>RW {r.kode}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} placeholder="Kode RT" className="h-11 font-mono" data-testid="rt-kode-input" />
        <Input value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} placeholder="Ket (opsional)" className="h-11" />
        <Button type="submit" className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="add-rt-button">
          <Plus className="h-4 w-4 mr-2" /> Tambah RT
        </Button>
      </form>

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterDusun} onValueChange={(v) => { setFilterDusun(v); setFilterRw("all"); }}>
          <SelectTrigger className="h-10 w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Dusun</SelectItem>
            {dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterRw} onValueChange={setFilterRw} disabled={filterDusun === "all"}>
          <SelectTrigger className="h-10 w-40"><SelectValue placeholder="Semua RW" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua RW</SelectItem>
            {rwsFiltered.map((r) => <SelectItem key={r.id} value={r.kode}>RW {r.kode}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Dusun</th>
              <th className="text-left px-4 py-3 font-semibold">RW</th>
              <th className="text-left px-4 py-3 font-semibold">Kode RT</th>
              <th className="text-left px-4 py-3 font-semibold">Keterangan</th>
              <th className="text-right px-4 py-3 font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Belum ada RT.</td></tr>}
            {filtered.map((rt) => (
              <tr key={rt.id} className="border-t border-slate-200">
                <td className="px-4 py-3">{rt.dusun}</td>
                <td className="px-4 py-3 font-mono">RW {rt.rw}</td>
                <td className="px-4 py-3 font-mono font-bold">RT {rt.kode}</td>
                <td className="px-4 py-3 text-slate-600">{rt.keterangan || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => del(rt)} className="text-red-700"><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TpsTab({ dusuns, tpsList, reload }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPanitia = user?.role === "panitia";
  const isPpdp = user?.role === "ppdp";
  const [form, setForm] = useState({ kode: "", nama: "", lokasi: "", dusun: "" });
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editing, setEditing] = useState(null); // { id, kode, nama, lokasi, dusun }

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const clearSelection = () => setSelectedIds(new Set());

  const add = async (e) => {
    e.preventDefault();
    if (!form.kode || !form.nama) { toast.error("Kode dan Nama TPS wajib"); return; }
    try { await api.post("/tps", form); toast.success("TPS ditambahkan."); setForm({ kode: "", nama: "", lokasi: "", dusun: "" }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Hapus ${t.kode}?`)) return;
    try { await api.delete(`/tps/${t.id}`); toast.success("Dihapus."); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const saveEdit = async () => {
    if (!editing) return;
    const nama = (editing.nama || "").trim();
    if (!nama) { toast.error("Nama TPS tidak boleh kosong"); return; }
    try {
      await api.put(`/tps/${editing.id}`, {
        nama,
        lokasi: (editing.lokasi || "").trim() || null,
      });
      toast.success(`${editing.kode} diperbarui.`);
      setEditing(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const sync = async () => {
    if (!window.confirm("Sinkronkan TPS dari data pemilih?\n\nSistem akan mengelompokkan pemilih berdasarkan (Dusun, TPS) dan membuat master TPS yang belum ada. Data yang sudah ada akan dilewati.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/tps/sync-from-voters");
      toast.success(`Sinkronisasi selesai. Dibuat: ${data.created}, Dilewati: ${data.skipped}.`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSyncing(false); }
  };

  // -------- Lock workflow actions --------
  const downloadBA = async (t, kind = "dpshp") => {
    try {
      const token = localStorage.getItem("sip_access_token");
      const path = kind === "dpt" ? `ba-dpt.pdf` : `ba-dpshp.pdf`;
      const url = `${api.defaults.baseURL}/tps/${t.id}/${path}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `BA-${kind.toUpperCase()}-${t.kode}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`BA ${kind.toUpperCase()} ${t.kode} berhasil diunduh.`);
    } catch (err) { toast.error("Gagal unduh BA: " + err.message); }
  };

  const uploadSignedBA = (t, kind = "dpshp") => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf,image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data: rec } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const endpoint = kind === "dpt" ? "ba-dpt/upload-signed" : "ba-dpshp/upload-signed";
        await api.post(`/tps/${t.id}/${endpoint}`, { file_id: rec.id });
        toast.success(`BA ${kind.toUpperCase()} TTD untuk ${t.kode} diupload. Menunggu pengesahan Panitia.`);
        reload();
      } catch (err) { toast.error(formatApiError(err)); }
    };
    input.click();
  };

  const sahkanDpshp = async (t) => {
    if (!window.confirm(`Sahkan ${t.kode} ke fase DPSHP final?\n\nSetelah disahkan, voter di TPS ini terkunci sampai admin unlock.`)) return;
    try {
      await api.post(`/tps/${t.id}/dpshp-verify`);
      toast.success(`${t.kode} disahkan ke DPSHP.`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sahkanDpt = async (t) => {
    if (!window.confirm(`Sahkan ${t.kode} ke fase DPT FINAL?\n\nIni adalah kunci terakhir. Voter di TPS ini akan menjadi DPT resmi untuk Pilkades.`)) return;
    try {
      await api.post(`/tps/${t.id}/dpt-verify`);
      toast.success(`${t.kode} disahkan ke DPT final.`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const unlock = async (t) => {
    const reason = window.prompt(`Alasan unlock ${t.kode}? (min. 10 karakter)`);
    if (!reason || reason.trim().length < 10) {
      if (reason !== null) toast.error("Alasan minimal 10 karakter.");
      return;
    }
    try {
      await api.post(`/tps/${t.id}/unlock`, { reason });
      toast.success(`${t.kode} ter-unlock kembali ke DPS.`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkForceLock = async () => {
    if (selectedIds.size === 0) { toast.error("Pilih minimal 1 TPS."); return; }
    const reason = window.prompt(`Force-lock ${selectedIds.size} TPS ke DPSHP. Catatan alasan? (min 10 karakter)`);
    if (!reason || reason.trim().length < 10) { if (reason !== null) toast.error("Alasan minimal 10 karakter."); return; }
    try {
      const { data } = await api.post("/tps/bulk-force-lock", { tps_ids: Array.from(selectedIds), reason });
      toast.success(`${data.locked} TPS berhasil di-lock.`);
      clearSelection();
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkUnlock = async () => {
    if (selectedIds.size === 0) { toast.error("Pilih minimal 1 TPS."); return; }
    const reason = window.prompt(`Unlock ${selectedIds.size} TPS. Catatan alasan? (min 10 karakter)`);
    if (!reason || reason.trim().length < 10) { if (reason !== null) toast.error("Alasan minimal 10 karakter."); return; }
    try {
      const { data } = await api.post("/tps/bulk-unlock", { tps_ids: Array.from(selectedIds), reason });
      toast.success(`${data.unlocked} TPS ter-unlock.`);
      clearSelection();
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const PHASE_BADGE = {
    DPS: { label: "DPS", className: "bg-slate-200 text-slate-700" },
    BA_UPLOADED: { label: "Menunggu Sahkan DPSHP", className: "bg-amber-200 text-amber-900" },
    DPSHP: { label: "DPSHP 🔒", className: "bg-emerald-700 text-white" },
    DPT_BA_UPLOADED: { label: "Menunggu Sahkan DPT", className: "bg-blue-200 text-blue-900" },
    DPT: { label: "DPT 🔒", className: "bg-blue-900 text-white" },
  };

  // group by dusun for display
  const grouped = (tpsList || []).reduce((acc, t) => {
    const key = t.dusun || "(Tanpa Dusun)";
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const dusunKeys = Object.keys(grouped).sort();
  const totalPemilih = (tpsList || []).reduce((sum, t) => sum + (t.jumlah_pemilih || 0), 0);
  const lockedCount = (tpsList || []).filter((t) => t.phase === "DPSHP" || t.phase === "DPT").length;
  const hasUnfinalizedTps = (tpsList || []).some((t) => t.phase !== "DPT");

  return (
    <div className="space-y-4">
      {hasUnfinalizedTps && (
        <div
          className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="tps-sementara-notice"
        >
          <Info className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            <p className="font-semibold">Data TPS bersifat sementara</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Selama tahap <b>DPS</b> dan <b>DPSHP</b>, satu kode TPS dapat muncul di lebih dari satu dusun (misal TPS-031 di Dusun Barat dan Dusun Tengah). Master TPS akan dikonsolidasikan dan dibagi ulang berdasarkan kode unik global setelah <b>DPT</b> ditetapkan.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Sinkronisasi</p>
          <p className="text-sm text-slate-700">Buat master TPS otomatis dari data pemilih yang sudah masuk, dikelompokkan per Dusun.</p>
        </div>
        <Button onClick={sync} disabled={syncing} className="bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="sync-tps-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Menyinkronkan..." : "Sinkronkan dari Data Pemilih"}
        </Button>
      </div>

      {(isAdmin || isPanitia) && (
        <form onSubmit={add} className="rounded-md border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} placeholder="Kode (TPS-001)" className="h-11 font-mono" data-testid="tps-kode-input" />
          <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Nama TPS" className="h-11" data-testid="tps-nama-input" />
          <Input value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} placeholder="Lokasi" className="h-11" />
          <Select value={form.dusun} onValueChange={(v) => setForm({ ...form, dusun: v })}>
            <SelectTrigger className="h-11"><SelectValue placeholder="Dusun" /></SelectTrigger>
            <SelectContent>{dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="add-tps-button">
            <Plus className="h-4 w-4 mr-2" /> Tambah TPS
          </Button>
        </form>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
          Total {tpsList.length} TPS · {totalPemilih.toLocaleString("id-ID")} Pemilih · {lockedCount} terkunci
        </p>
        {isAdmin && selectedIds.size > 0 && (
          <div className="flex items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded-md text-sm" data-testid="bulk-action-bar">
            <span>{selectedIds.size} dipilih</span>
            <Button size="sm" onClick={bulkForceLock} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="bulk-force-lock-btn"><Lock className="h-3 w-3 mr-1" />Force Lock</Button>
            <Button size="sm" onClick={bulkUnlock} className="bg-red-600 hover:bg-red-700 h-8" data-testid="bulk-unlock-btn"><Unlock className="h-3 w-3 mr-1" />Bulk Unlock</Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} className="text-white hover:bg-slate-800 h-8">Batal</Button>
          </div>
        )}
      </div>

      {tpsList.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white px-4 py-8 text-center text-slate-500">
          Belum ada TPS. Klik <span className="font-semibold">Sinkronkan dari Data Pemilih</span> untuk membuat master TPS otomatis.
        </div>
      )}

      {dusunKeys.map((dk) => {
        const items = grouped[dk];
        const subtotal = items.reduce((s, t) => s + (t.jumlah_pemilih || 0), 0);
        return (
          <div key={dk} className="rounded-md border border-slate-200 bg-white overflow-hidden" data-testid={`tps-group-${dk}`}>
            <div className="flex items-center justify-between bg-slate-100 px-4 py-2 border-b border-slate-200">
              <div className="font-semibold text-slate-800">{dk}</div>
              <div className="text-xs text-slate-600">{items.length} TPS · {subtotal.toLocaleString("id-ID")} pemilih</div>
            </div>
            <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <tr>
                  {isAdmin && <th className="px-3 py-3 w-8"></th>}
                  <th className="text-left px-3 py-3 font-semibold">Kode</th>
                  <th className="text-left px-3 py-3 font-semibold">Fase</th>
                  <th className="text-left px-3 py-3 font-semibold">Coklit</th>
                  <th className="text-right px-3 py-3 font-semibold">Pemilih</th>
                  <th className="text-right px-3 py-3 font-semibold">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const p = t.progress || {};
                  const phase = t.phase || "DPS";
                  const phaseBadge = PHASE_BADGE[phase] || PHASE_BADGE.DPS;
                  const fullyLocked = phase === "DPT";  // hanya DPT yang benar2 final tidak boleh ada aksi BA lagi
                  const lockedForVoters = phase === "DPSHP" || phase === "DPT" || phase === "BA_UPLOADED" || phase === "DPT_BA_UPLOADED";
                  return (
                    <tr key={t.id} className="border-t border-slate-200" data-testid={`tps-row-${t.kode}`}>
                      {isAdmin && (
                        <td className="px-3 py-3">
                          <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} data-testid={`tps-checkbox-${t.kode}`} />
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <div className="font-mono font-bold">{t.kode}</div>
                        <div className="text-xs text-slate-500">{t.lokasi || ""}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${phaseBadge.className}`} data-testid={`tps-phase-${t.kode}`}>{phaseBadge.label}</span>
                      </td>
                      <td className="px-3 py-3">
                        {p.active_voters !== undefined ? (
                          <div>
                            <div className="text-xs tabular-nums">{p.coklit_verified}/{p.active_voters} ({p.coklit_progress_pct}%)</div>
                            {p.pending_changes > 0 && <div className="text-xs text-amber-700">{p.pending_changes} pending</div>}
                          </div>
                        ) : "-"}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold tabular-nums">{(t.jumlah_pemilih || 0).toLocaleString("id-ID")}</td>
                      <td className="px-3 py-3 text-right space-x-1 whitespace-nowrap">
                        {/* DPSHP workflow buttons (phase = DPS) */}
                        {phase === "DPS" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => downloadBA(t, "dpshp")} title="Unduh BA DPSHP" data-testid={`download-ba-${t.kode}`}>
                              <FileDown className="h-4 w-4" />
                            </Button>
                            {(isPpdp || isPanitia || isAdmin) && p.ready_for_lock && (
                              <Button variant="ghost" size="sm" onClick={() => uploadSignedBA(t, "dpshp")} title="Upload BA DPSHP Tertandatangan" className="text-emerald-700" data-testid={`upload-ba-${t.kode}`}>
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {/* BA DPSHP awaiting Panitia verify */}
                        {phase === "BA_UPLOADED" && (isPanitia || isAdmin) && (
                          <Button variant="ghost" size="sm" onClick={() => sahkanDpshp(t)} title="Sahkan DPSHP" className="text-emerald-700" data-testid={`sahkan-dpshp-${t.kode}`}>
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                        )}
                        {/* DPT workflow buttons (phase = DPSHP) */}
                        {phase === "DPSHP" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => downloadBA(t, "dpt")} title="Unduh BA DPT Final" className="text-blue-800" data-testid={`download-ba-dpt-${t.kode}`}>
                              <FileDown className="h-4 w-4" />
                            </Button>
                            {(isPpdp || isPanitia || isAdmin) && (
                              <Button variant="ghost" size="sm" onClick={() => uploadSignedBA(t, "dpt")} title="Upload BA DPT Tertandatangan" className="text-blue-800" data-testid={`upload-ba-dpt-${t.kode}`}>
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {/* BA DPT awaiting Panitia verify */}
                        {phase === "DPT_BA_UPLOADED" && (isPanitia || isAdmin) && (
                          <Button variant="ghost" size="sm" onClick={() => sahkanDpt(t)} title="Sahkan DPT FINAL" className="text-blue-800" data-testid={`sahkan-dpt-${t.kode}`}>
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Unlock admin (semua locked phase) */}
                        {lockedForVoters && isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => unlock(t)} title="Unlock" className="text-amber-700" data-testid={`unlock-tps-${t.kode}`}>
                            <Unlock className="h-4 w-4" />
                          </Button>
                        )}
                        {(isAdmin || isPanitia) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditing({ id: t.id, kode: t.kode, nama: t.nama || t.kode, lokasi: t.lokasi || "", dusun: t.dusun || "" })}
                            title="Edit nama & lokasi TPS"
                            data-testid={`edit-tps-${t.kode}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {isAdmin && phase === "DPS" && !fullyLocked && (
                          <Button variant="ghost" size="sm" onClick={() => del(t)} className="text-red-700" data-testid={`delete-tps-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        );
      })}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
          data-testid="edit-tps-modal"
        >
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Edit TPS</div>
                <div className="font-display text-lg font-bold mt-0.5">{editing.kode}</div>
                <div className="text-xs text-slate-500">{editing.dusun}</div>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-slate-400 hover:text-slate-700 text-xl leading-none px-2"
                data-testid="edit-tps-close"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Nama TPS</label>
                <Input
                  value={editing.nama}
                  onChange={(e) => setEditing({ ...editing, nama: e.target.value })}
                  placeholder="Misal: TPS 1 Dusun Barat"
                  className="h-11"
                  data-testid="edit-tps-nama"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Alamat / Lokasi TPS</label>
                <textarea
                  value={editing.lokasi}
                  onChange={(e) => setEditing({ ...editing, lokasi: e.target.value })}
                  placeholder="Contoh: Balai Dusun Barat, Jl. Mawar No. 5, RT 002/RW 001"
                  rows={3}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="edit-tps-lokasi"
                />
                <p className="text-xs text-slate-500">
                  Alamat ini akan ditampilkan di hasil pencarian publik agar pemilih mengetahui lokasi TPS-nya.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <Button variant="outline" onClick={() => setEditing(null)} className="h-10" data-testid="edit-tps-cancel">Batal</Button>
              <Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800 h-10" data-testid="edit-tps-save">
                <Pencil className="h-4 w-4 mr-2" /> Simpan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
